import { useState, ChangeEvent, useCallback } from 'react';

import { DateRangePicker, Select, Table, Toolbar, Spinner, Pagination, SearchFilter, ITableRequiredParameters } from '@netfront/ui-library';

import { ReportTableProps } from './ReportTable.interfaces';

import { REPORTING_PAGE_CONSTANTS } from '../../ReportingPage.constants';

const { tableSelectionItems, viewSelectionItems } = REPORTING_PAGE_CONSTANTS;

const ReportTable = <T extends ITableRequiredParameters>({
  selectedTable,
  onTableChange,
  selectedView,
  onViewChange, 
  dateRange,
  onDateRangeChange,
  data = [],
  columns = [],
  isLoading = false,
  emptyStateMessage = 'There are no pdf usage data available yet',
  handleSearch,
  totalItems = 0,
  pageSize = 10,
  isLoadMoreDisabled = false,
  handleOnPageSizeChange,
  handleOnPaginate,
  childrenEnd,
}: ReportTableProps<T>) => {

  const [searchValue, setSearchValue] = useState('');

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;

    setSearchValue(value);
  };

  const getFilteredItems = useCallback(() => {
    handleSearch(searchValue);
  }, [handleSearch, searchValue]);

  const handleClearSearch = () => {
    setSearchValue('');
    handleSearch('');
  };
  

  return (
    <>
      <Spinner isLoading={isLoading} />

      <div className="px-4 w-full">
        <Toolbar
          additionalClassNames="c-reporting-page__table-toolbar"
          childrenEnd={childrenEnd}
          childrenMiddle={
            <div className="c-reporting-page__table-toolbar__middle flex flex-1 gap-4">
              <DateRangePicker
                dateInputProps={{
                  labelText: 'Select date range',
                  id: 'id_date_selector',
                  name: 'date_range_selector',
                  type: 'text',
                  isLabelHidden: true,
                  hasPadding: false,
                }}
                selectedDates={dateRange}
                onSelected={onDateRangeChange}
              />
              <Select
                id="Select table"
                labelText=""
                name="Select table"
                options={tableSelectionItems}
                value={selectedTable}
                isLabelHidden
                onChange={(event: ChangeEvent<HTMLSelectElement>) => onTableChange(event)}
              />
              <Select
                id="Select view"
                labelText=""
                name="Select view"
                options={viewSelectionItems}
                value={selectedView}
                isLabelHidden
                onChange={(event: ChangeEvent<HTMLSelectElement>) => onViewChange(event)}
              />
            </div>
          }
          childrenStart={
            <SearchFilter
              additionalClassNames="c-search-filter"
              id="search"
              labelText="Pdf filter"
              name="search"
              placeholder="Search"
              type="text"
              value={searchValue}
              hasSearchButton
              isLabelHidden
              onChange={handleChange}
              onClear={handleClearSearch}
              onSearch={getFilteredItems}
            />
          }
        />

        <>
          {!data.length && <p className="mt-8">{emptyStateMessage}</p>}

          {Boolean(data.length) && (
            <>
              <div className="mt-8">
                <Table<T> columns={columns} data={data} />
              </div>

              {totalItems > pageSize && (
                  <Pagination
                    isLoadMoreDisabled={isLoadMoreDisabled}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    onPageChange={handleOnPaginate}
                    onPageSizeChange={handleOnPageSizeChange}
                  />
                )}
            </>
          )}
        </>
      </div>
    </>
  );
};

export { ReportTable };
